/*
 * TTT "Add a pet" add-on — swipeable carousel edition.
 * - Presents pet attachments ONE-AT-A-TIME in a horizontal carousel the shopper
 *   can swipe / drag / chevron / keyboard through.
 * - Each slide carries its own personalisation inputs (pet name, text fields...) inherited from
 *   that attachment product. Those inputs are NOT part of the parent form; this script reads
 *   them per-pet when adding to cart so each pet line item carries its own properties.
 * - Live-updates the main Add-to-cart button label with the combined total (base + selected pet).
 * - On a single ATC click with a pet selected, adds the parent variant AND the pet in ONE
 *   POST /cart/add.js request, with the pet carrying `_added_to` (parent SKU/handle)
 *   plus its own personalisation properties.
 * - Progressive enhancement: with no pet selected, the theme's normal product-form flow runs.
 */
(function () {
  'use strict';

  var CURRENCY_SYMBOL = '€'; // fallback only — TTT is a EUR store
  var api = window.TTTAddAPet = window.TTTAddAPet || {};
  window.TTTAddAPet = api;
  api.instances = api.instances || {};

  function getFormId(form) {
    return typeof form === 'string' ? form : form && form.getAttribute && form.getAttribute('id');
  }

  api.submit = function (form) {
    var formId = getFormId(form);
    var instance = formId && api.instances[formId];
    return !!(instance && instance.submit(form));
  };

  // Derive the shop's real money format (symbol, decimal/thousands separators,
  // symbol placement) from a Liquid-rendered sample so the button total matches
  // the store format (e.g. "12,34 €" vs "€12.34"). Falls back safely on any
  // parse failure so this can never render NaN.
  var MONEY_FMT = (function () {
    var el = document.querySelector('[data-ttt-money-sample]');
    var sample = el && el.getAttribute('data-ttt-money-sample'); // e.g. "€1,234.56" | "1.234,56 €"
    if (!sample) return null;
    try {
      var symbol = sample.replace(/[\d.,\s ]/g, ''); // strip digits/separators/spaces -> symbol or code
      var suffix = sample.trim().lastIndexOf(symbol) > 0; // symbol at the end?
      var numeric = sample.replace(symbol, '').replace(/[\s ]/g, '');
      var lastDot = numeric.lastIndexOf('.');
      var lastComma = numeric.lastIndexOf(',');
      var decSep = '.', thouSep = ',';
      if (lastComma > lastDot) { decSep = ','; thouSep = '.'; }
      else if (lastDot === -1 && lastComma === -1) { thouSep = ''; }
      return { symbol: symbol, suffix: suffix, decSep: decSep, thouSep: thouSep };
    } catch (e) { return null; }
  })();

  function formatMoney(cents) {
    var amount = (Math.round(cents) / 100).toFixed(2);
    if (!MONEY_FMT) return CURRENCY_SYMBOL + amount;
    var parts = amount.split('.');
    var intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, MONEY_FMT.thouSep);
    var num = intPart + MONEY_FMT.decSep + parts[1];
    return MONEY_FMT.suffix ? num + ' ' + MONEY_FMT.symbol : MONEY_FMT.symbol + num;
  }

  function cartAddUrl() {
    if (window.theme && theme.routes && theme.routes.cartAdd) return theme.routes.cartAdd;
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return root + 'cart/add.js';
  }

  function afterAtcMode() {
    return (window.theme && theme.settings && theme.settings.afterAtc) || 'drawer';
  }

  function cartUrl() {
    if (window.theme && theme.routes && theme.routes.cart) return theme.routes.cart;
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return root + 'cart';
  }

  function cartJsUrl() {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return root + 'cart.js';
  }

  // How many cart lines already carry this SKU (so the next one is the Nth).
  function fetchCartLineCount(sku) {
    if (!sku) return Promise.resolve(0);
    return fetch(cartJsUrl(), { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var n = 0;
        (cart.items || []).forEach(function (it) { if (it.sku === sku) n += 1; });
        return n;
      })
      .catch(function () { return 0; });
  }

  function setupCarousel(root) {
    var track = root.querySelector('[data-pet-track]');
    if (!track) return null;

    var viewport = root.querySelector('[data-pet-viewport]');
    var slides = Array.prototype.slice.call(track.children);
    var prevBtn = root.querySelector('[data-pet-prev]');
    var nextBtn = root.querySelector('[data-pet-next]');
    var dotsWrap = root.querySelector('[data-pet-dots]');
    var dots = [];
    var current = 0;

    function clamp(i) {
      return Math.max(0, Math.min(slides.length - 1, i));
    }

    function scrollToIndex(i) {
      current = clamp(i);
      syncControls();
    }

    function syncControls() {
      var slideWidth = viewport ? viewport.getBoundingClientRect().width : track.getBoundingClientRect().width;
      track.style.setProperty('--ttt-ap-offset', (current * slideWidth * -1) + 'px');
      if (prevBtn) prevBtn.disabled = current <= 0;
      if (nextBtn) nextBtn.disabled = current >= slides.length - 1;
      slides.forEach(function (slide, i) {
        if (i === current) {
          slide.removeAttribute('aria-hidden');
        } else {
          slide.setAttribute('aria-hidden', 'true');
        }
      });
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === current);
      });
    }

    // Build dots (hidden for a single slide).
    if (dotsWrap && slides.length > 1) {
      slides.forEach(function (slide, i) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'ttt-add-a-pet__dot';
        dot.setAttribute('aria-label', 'Go to attachment ' + (i + 1));
        dot.addEventListener('click', function () { scrollToIndex(i); });
        dotsWrap.appendChild(dot);
        dots.push(dot);
      });
      dotsWrap.removeAttribute('aria-hidden');
    }

    if (prevBtn) prevBtn.addEventListener('click', function () { scrollToIndex(current - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { scrollToIndex(current + 1); });

    track.addEventListener('keydown', function (evt) {
      if (evt.key === 'ArrowLeft') {
        evt.preventDefault();
        scrollToIndex(current - 1);
      } else if (evt.key === 'ArrowRight') {
        evt.preventDefault();
        scrollToIndex(current + 1);
      }
    });

    if (viewport && window.PointerEvent) {
      var dragStart = null;
      viewport.addEventListener('pointerdown', function (evt) {
        if (evt.target.closest('button, input, textarea, select, a')) return;
        dragStart = evt.clientX;
        viewport.setPointerCapture(evt.pointerId);
      });
      viewport.addEventListener('pointerup', function (evt) {
        if (dragStart == null) return;
        var distance = evt.clientX - dragStart;
        dragStart = null;
        if (Math.abs(distance) < 40) return;
        scrollToIndex(current + (distance < 0 ? 1 : -1));
      });
      viewport.addEventListener('pointercancel', function () {
        dragStart = null;
      });
    }

    if (viewport && window.ResizeObserver) {
      var observer = new ResizeObserver(syncControls);
      observer.observe(viewport);
    } else {
      window.addEventListener('resize', syncControls);
    }

    syncControls();

    return {
      slides: slides,
      scrollToIndex: scrollToIndex,
      indexOf: function (slide) { return slides.indexOf(slide); },
      markSelected: function (i, selected) {
        var dot = dots[i];
        if (dot) dot.classList.toggle('is-selected', selected);
      }
    };
  }

  // Char counters + numeric-only year field + "No thanks" clear, scoped to the add-on.
  function wirePetFieldBehaviours(root) {
    root.querySelectorAll('[data-max-length]').forEach(function (input) {
      var counter = root.querySelector('[data-counter-for="' + input.id + '"]');
      if (!counter) return;
      var maxLen = parseInt(input.getAttribute('data-max-length'), 10);
      input.addEventListener('input', function () {
        counter.textContent = this.value.length;
        counter.style.color = this.value.length >= maxLen ? 'var(--ttt-ap-accent, #8b1a1a)' : '';
      });
    });

    root.querySelectorAll('.ttt-add-a-pet__input--year').forEach(function (input) {
      input.addEventListener('input', function () {
        var pos = this.selectionStart;
        this.value = this.value.replace(/[^0-9]/g, '').slice(0, 4);
        this.setSelectionRange(pos, pos);
      });
    });

    // "No thanks" year button: subtle/greyed when a year is present, red/active
    // (i.e. "no year" chosen) when the field is empty. Keeps the 2026 default.
    root.querySelectorAll('.ttt-add-a-pet__year-group').forEach(function (group) {
      var input = group.querySelector('.ttt-add-a-pet__input--year');
      var clearBtn = group.querySelector('[data-year-clear]');
      if (!input || !clearBtn) return;
      function syncYearState() {
        var hasYear = (input.value || '').trim() !== '';
        clearBtn.classList.toggle('is-active', !hasYear);
        clearBtn.setAttribute('aria-pressed', hasYear ? 'false' : 'true');
      }
      input.addEventListener('input', syncYearState);
      clearBtn.addEventListener('click', function () {
        // Clear + blur (not focus): on mobile, focusing re-opens the number
        // keyboard. Blur dismisses it so the shopper can move on.
        input.value = '';
        input.blur();
        syncYearState();
      });
      syncYearState();
    });
  }

  function init(root) {
    var formId = root.getAttribute('data-form-id');
    if (!formId) return;

    var basePrice = parseInt(root.getAttribute('data-base-price'), 10) || 0;
    var parentSku = root.getAttribute('data-parent-sku') || '';
    var parentTitle = root.getAttribute('data-parent-title') || '';
    var toggles = root.querySelectorAll('.ttt-add-a-pet__toggle');
    var summaryWrap = root.querySelector('[data-pet-summary]');
    var summaryList = root.querySelector('[data-pet-summary-list]');
    var bridgedButton = null;

    var carousel = setupCarousel(root);
    wirePetFieldBehaviours(root);

    function getForm() {
      return document.getElementById(formId);
    }

    function getButton() {
      var form = getForm();
      return form ? form.querySelector('[name="add"]') : null;
    }

    // Committed attachments — each press of "Add" pushes a SNAPSHOT here, so the
    // same attachment/variant can be added several times with DIFFERENT names
    // (e.g. a black dog "Rufus" and a black dog "Jimmy"). Editing a slide afterwards
    // never touches already-added snapshots.
    // Shape: { variantId, price, petTitle, colour, name, qty, properties }
    var addedItems = [];
    function hasAdded() { return addedItems.length > 0; }

    // Quantity chosen for a pet slide (defaults to 1, clamped 1..max).
    function petQty(slide) {
      var input = slide && slide.querySelector('[data-pet-qty]');
      var v = input ? parseInt(input.value, 10) : 1;
      if (isNaN(v) || v < 1) v = 1;
      var max = input ? parseInt(input.getAttribute('max'), 10) : 20;
      if (max && v > max) v = max;
      return v;
    }

    function total() {
      var t = basePrice;
      addedItems.forEach(function (it) { t += it.price * it.qty; });
      return t;
    }

    // Append the running total (base + selected pets) to the ATC button label.
    // We deliberately do NOT bail on aria-disabled: the personalisation confirmation
    // gate (ttt-personalisation) sets aria-disabled until the shopper ticks the confirm
    // box, and the price must keep updating during that time. We only skip a HARD
    // disabled button (sold out / no variant selected), whose label ("Sold out") must
    // not have a price appended to it.
    function updateLabel() {
      var btn = getButton();
      if (!btn || btn.disabled) return;
      // Prefer the theme's stored base label; fall back to the current text with any
      // previously-appended " · <price>" stripped so totals never compound.
      var baseText = btn.getAttribute('data-add-to-cart-text');
      if (!baseText) baseText = (btn.textContent || '').split(' · ')[0];
      if (basePrice > 0) {
        btn.textContent = baseText + ' · ' + formatMoney(total());
      } else {
        btn.textContent = baseText;
      }
    }

    // After adding, clear the slide so the shopper can enter a different name for
    // the next one (year returns to its 2026 default, qty back to 1).
    function resetSlide(slide) {
      slide.querySelectorAll('[data-pet-prop]').forEach(function (input) {
        if (input.classList.contains('ttt-add-a-pet__input--year')) {
          input.value = '2026';
        } else {
          input.value = '';
        }
        input.classList.remove('is-invalid');
        var counter = root.querySelector('[data-counter-for="' + input.id + '"]');
        if (counter) counter.textContent = '0';
      });
      var qtyInput = slide.querySelector('[data-pet-qty]');
      if (qtyInput) qtyInput.value = 1;
      var yearBtn = slide.querySelector('[data-year-clear]');
      if (yearBtn) { yearBtn.classList.remove('is-active'); yearBtn.setAttribute('aria-pressed', 'false'); }
    }

    // The pet's entered personalisation (excluding the pre-filled year), joined.
    function petDisplayName(slide) {
      var parts = [];
      slide.querySelectorAll('[data-pet-prop]').forEach(function (input) {
        if (input.classList.contains('ttt-add-a-pet__input--year')) return;
        var v = (input.value || '').trim();
        if (v) parts.push(v);
      });
      return parts.join(', ');
    }

    function selectedSwatchLabel(slide) {
      var sw = slide.querySelector('[data-pet-variant].is-selected') || slide.querySelector('[data-pet-variant]');
      return sw ? (sw.getAttribute('title') || sw.getAttribute('data-swatch') || '') : '';
    }

    // "Added attachments" list below the carousel, built from the committed
    // snapshots: name, colour, qty + a Remove button per entry.
    function renderSummary() {
      if (!summaryWrap || !summaryList) return;
      summaryList.innerHTML = '';
      if (addedItems.length === 0) { summaryWrap.hidden = true; return; }
      summaryWrap.hidden = false;

      addedItems.forEach(function (it) {
        var label = it.petTitle;
        if (it.colour) label += ' · ' + it.colour;
        if (it.qty > 1) label += ' ×' + it.qty;

        var li = document.createElement('li');
        li.className = 'ttt-add-a-pet__added-item';

        var text = document.createElement('span');
        text.className = 'ttt-add-a-pet__added-text';
        var strong = document.createElement('span');
        strong.className = 'ttt-add-a-pet__added-name';
        strong.textContent = label;
        text.appendChild(strong);
        if (it.name) {
          var sub = document.createElement('span');
          sub.className = 'ttt-add-a-pet__added-sub';
          sub.textContent = it.name;
          text.appendChild(sub);
        }

        var remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'ttt-add-a-pet__added-remove';
        remove.setAttribute('aria-label', 'Remove ' + it.petTitle);
        remove.textContent = 'Remove';
        remove.addEventListener('click', function () {
          var i = addedItems.indexOf(it);
          if (i > -1) addedItems.splice(i, 1);
          renderSummary();
          updateLabel();
        });

        li.appendChild(text);
        li.appendChild(remove);
        summaryList.appendChild(li);
      });
    }

    // A slide is "dirty" when the shopper has typed personalisation but hasn't
    // pressed Add. Those must be resolved (added or cleared) before the main ATC.
    function slideHasText(slide) {
      var inputs = slide.querySelectorAll('[data-pet-prop]');
      for (var i = 0; i < inputs.length; i++) {
        // Ignore the year field — it's pre-filled with the default year, so it
        // doesn't count as the shopper having started an attachment.
        if (inputs[i].classList.contains('ttt-add-a-pet__input--year')) continue;
        if ((inputs[i].value || '').trim()) return true;
      }
      return false;
    }

    function dirtySlides() {
      var out = [];
      root.querySelectorAll('[data-pet-slide]').forEach(function (slide) {
        if (slideHasText(slide)) out.push(slide);
      });
      return out;
    }

    // Validate a slide for adding: required personalisation filled + a variant
    // chosen. Returns the first invalid field (or null when OK).
    function validateSlideForAdd(slide) {
      var collected = collectPetProperties(slide);
      if (collected.firstInvalid) return collected.firstInvalid;
      var toggle = slide.querySelector('.ttt-add-a-pet__toggle');
      if (!toggle || !toggle.getAttribute('data-variant-id')) return toggle;
      return null;
    }

    function bridgeAtcClick(evt) {
      if (!hasAdded() && dirtySlides().length === 0) return;

      // Some theme/app scripts hook the button click before the form submit.
      // When an attachment is added (or dirty and must be resolved), keep those
      // single-item handlers out and let the submit event below decide.
      evt.stopImmediatePropagation();
    }

    function wireAtcClickBridge() {
      var btn = getButton();
      if (!btn || btn === bridgedButton) return;
      if (bridgedButton) bridgedButton.removeEventListener('click', bridgeAtcClick, true);
      bridgedButton = btn;
      bridgedButton.addEventListener('click', bridgeAtcClick, true);
    }

    function bundledSubmit(form) {
      form = form || getForm();
      if (!form || getFormId(form) !== formId) return false;
      if (!hasAdded()) return false;

      var btn = getButton();
      // Respect the confirmation gate (ttt-personalisation) / any disabled state.
      if (btn && (btn.disabled || btn.getAttribute('aria-disabled') === 'true')) return true;

      addWithPets(form, btn);
      return true;
    }

    // "Add" commits a snapshot of the current slide, then clears it so the shopper
    // can enter the next one (different name, etc.).
    toggles.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var slide = btn.closest('[data-pet-slide]');
        var invalid = validateSlideForAdd(slide);
        if (invalid) {
          if (invalid.classList) invalid.classList.add('is-invalid');
          setError(getForm(), 'Please complete the required details for this attachment before adding it.');
          if (invalid.focus) setTimeout(function () { invalid.focus(); }, 50);
          return;
        }
        setError(getForm(), false);

        var collected = collectPetProperties(slide);
        var titleEl = slide.querySelector('.ttt-add-a-pet__title');
        addedItems.push({
          variantId: btn.getAttribute('data-variant-id'),
          price: parseInt(btn.getAttribute('data-price'), 10) || 0,
          petTitle: titleEl ? titleEl.textContent.trim() : 'Attachment',
          colour: selectedSwatchLabel(slide),
          name: petDisplayName(slide),
          qty: petQty(slide),
          properties: collected.properties
        });

        resetSlide(slide);
        renderSummary();
        updateLabel();

        // Brief confirmation flash.
        btn.classList.add('is-just-added');
        setTimeout(function () { btn.classList.remove('is-just-added'); }, 900);
      });
    });

    // Quantity steppers — clamp only (qty is captured into the snapshot on Add).
    root.querySelectorAll('[data-pet-qty-wrap]').forEach(function (wrap) {
      var input = wrap.querySelector('[data-pet-qty]');
      if (!input) return;
      var decr = wrap.querySelector('[data-qty-decr]');
      var incr = wrap.querySelector('[data-qty-incr]');
      function setQty(v) {
        if (isNaN(v) || v < 1) v = 1;
        var max = parseInt(input.getAttribute('max'), 10) || 20;
        if (v > max) v = max;
        input.value = v;
      }
      if (decr) decr.addEventListener('click', function () { setQty((parseInt(input.value, 10) || 1) - 1); });
      if (incr) incr.addEventListener('click', function () { setQty((parseInt(input.value, 10) || 1) + 1); });
      input.addEventListener('input', function () { setQty(parseInt(input.value, 10)); });
    });

    // Variant / colour swatches: selecting one repoints this pet's toggle at the
    // chosen variant (id + price), updates the price label + main image, and — if
    // the pet is already added — refreshes the running ATC total.
    function selectVariant(swatch) {
      if (swatch.getAttribute('data-unavailable') === 'true') return;
      var slide = swatch.closest('[data-pet-slide]');
      var group = swatch.closest('[data-pet-variants]');
      if (!slide || !group) return;

      group.querySelectorAll('[data-pet-variant]').forEach(function (s) {
        s.classList.remove('is-selected');
        s.setAttribute('aria-checked', 'false');
      });
      swatch.classList.add('is-selected');
      swatch.setAttribute('aria-checked', 'true');

      var price = swatch.getAttribute('data-price');
      var toggle = slide.querySelector('.ttt-add-a-pet__toggle');
      if (toggle) {
        toggle.setAttribute('data-variant-id', swatch.getAttribute('data-variant-id'));
        if (price != null) toggle.setAttribute('data-price', price);
      }

      var priceEl = slide.querySelector('[data-pet-price]');
      if (priceEl && price != null) {
        priceEl.textContent = '+' + formatMoney(parseInt(price, 10) || 0);
      }

      var img = swatch.getAttribute('data-variant-image');
      var mainImg = slide.querySelector('.ttt-add-a-pet__img');
      if (img && mainImg) {
        mainImg.setAttribute('src', img);
        mainImg.removeAttribute('srcset');
      }
    }

    root.querySelectorAll('[data-pet-variant]').forEach(function (swatch) {
      swatch.addEventListener('click', function () { selectVariant(swatch); });
    });

    // Keep base price / SKU / label in sync when the shopper changes variant.
    // The theme dispatches on:variant:change AFTER it has re-rendered the ATC button,
    // so re-applying the label here is safe.
    document.addEventListener('on:variant:change', function (evt) {
      var variant = evt.detail && evt.detail.variant;
      if (!variant) return;
      // Only react to changes for this product's form.
      var form = evt.detail.form;
      if (form && getFormId(form) && getFormId(form) !== formId) return;
      basePrice = typeof variant.price === 'number' ? variant.price : basePrice;
      if (variant.sku) {
        parentSku = variant.sku;
      }
      // Defer slightly so it runs after the theme finishes updating the button.
      setTimeout(function () {
        updateLabel();
        wireAtcClickBridge();
      }, 0);
    });

    // Intercept the main product form submit in the capture phase so we run BEFORE
    // the theme's product-form handler. Only take over when a pet is selected.
    document.addEventListener('submit', function (evt) {
      var form = evt.target;
      if (!form || getFormId(form) !== formId) return;

      // Block the whole add-to-cart while any attachment has typed details but
      // wasn't added (or cleared).
      var dirty = dirtySlides();
      if (dirty.length > 0) {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        blockForDirty(form, dirty);
        return;
      }

      if (!hasAdded()) return; // no attachments added → normal flow

      evt.preventDefault();
      evt.stopImmediatePropagation();
      bundledSubmit(form);
    }, true);

    // Nudge the shopper to add or clear an attachment they started filling in.
    function blockForDirty(form, dirty) {
      setError(form, 'You added details to an attachment but didn’t add it. Press “Add” on it, or clear its details, before adding to your cart.');
      var slide = dirty[0];
      if (carousel && slide) carousel.scrollToIndex(carousel.indexOf(slide));
      var addBtn = slide && slide.querySelector('.ttt-add-a-pet__toggle');
      if (addBtn) setTimeout(function () { addBtn.focus(); }, 200);
    }

    function setError(form, message) {
      var errorEl = form.querySelector('.js-form-error');
      if (!errorEl) return;
      errorEl.hidden = !message;
      if (message) {
        errorEl.textContent = Array.isArray(message) ? message.join(', ') : message;
      }
    }

    // Collect a selected pet's personalisation into a line-item properties object.
    // Returns { properties, firstInvalid } — firstInvalid is set when a required
    // field for a SELECTED pet is empty.
    function collectPetProperties(slide) {
      // _added_to (hidden) = parent SKU for internal traceability.
      // "Added to" (visible) = parent title, shown to the shopper in cart/checkout
      // and on the packing slip as small grey text.
      var properties = { _added_to: parentSku, 'Added to': parentTitle };
      var firstInvalid = null;
      if (!slide) return { properties: properties, firstInvalid: null };

      slide.querySelectorAll('[data-pet-prop]').forEach(function (input) {
        var label = input.getAttribute('data-pet-prop');
        var value = (input.value || '').trim();
        var required = input.getAttribute('data-pet-required') === 'true';

        if (required && !value) {
          input.classList.add('is-invalid');
          if (!firstInvalid) firstInvalid = input;
        } else {
          input.classList.remove('is-invalid');
        }
        if (value) properties[label] = value;
      });

      return { properties: properties, firstInvalid: firstInvalid };
    }

    function addWithPets(form, btn) {
      setError(form, false);

      // Build parent item from the form (variant id, quantity, personalisation properties).
      var formData = new FormData(form);
      var parent = {
        id: formData.get('id'),
        quantity: parseInt(formData.get('quantity'), 10) || 1,
        properties: {}
      };
      formData.forEach(function (value, key) {
        var m = key.match(/^properties\[(.+)\]$/);
        if (m && value !== '') parent.properties[m[1]] = value;
      });

      if (!parent.id) {
        setError(form, 'Please choose an available option before adding this to your cart.');
        return;
      }

      // Attachment items = the committed snapshots (one per press of "Add").
      var petItems = addedItems.map(function (it) {
        return { id: it.variantId, quantity: it.qty, properties: it.properties };
      });
      if (petItems.some(function (p) { return !p.id; })) {
        setError(form, 'Sorry, this attachment is missing its variant. Please refresh and try again.');
        return;
      }

      if (btn) {
        btn.setAttribute('aria-disabled', 'true');
        btn.classList.add('is-loading');
      }
      if (window.theme && theme.settings && theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      // Sections to refresh (mirrors product-form.js behaviour).
      var sections = ['cart-icon-bubble'];
      var cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        var sectionEl = cartDrawer.closest('.shopify-section');
        if (sectionEl) sections.push(sectionEl.id.replace('shopify-section-', ''));
      }

      function loadSections(data) {
        return fetch(window.location.pathname + '?sections=' + sections.join(','))
          .then(function (response) { return response.json(); })
          .then(function (renderedSections) {
            var responseData = data && typeof data === 'object' ? data : {};
            responseData.sections = renderedSections;
            return responseData;
          });
      }

      // Human-readable reference: <parentSku>-<N>, where N counts this SKU in the
      // cart (1 = first decoration added, 2 = second, ...). Stamped on the parent
      // line and every attachment so the packing slip / cart can tie them together
      // and two identical SKUs never merge. Needs the current cart, so fetch first.
      fetchCartLineCount(parentSku).then(function (existing) {
        var groupRef = (parentSku || 'ITEM') + '-' + (existing + 1);
        parent.properties._ref = groupRef;
        petItems.forEach(function (p) { p.properties._added_to_ref = groupRef; });
        fireAdd([parent].concat(petItems));
      });

      function fireAdd(items) {
      var body = new FormData();
      items.forEach(function (item, index) {
        body.append('items[' + index + '][id]', item.id);
        body.append('items[' + index + '][quantity]', item.quantity);
        Object.keys(item.properties || {}).forEach(function (key) {
          body.append('items[' + index + '][properties][' + key + ']', item.properties[key]);
        });
      });
      body.append('sections', sections.join(','));
      body.append('sections_url', window.location.pathname);

      fetch(cartAddUrl(), {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: body
      })
        .then(function (response) {
          return response.json().then(function (data) {
            return { ok: response.ok, data: data };
          });
        })
        .then(function (result) {
          var data = result.data;
          if (!result.ok || data.status) {
            var error = typeof data.description === 'string' ? data.description : data.message;
            if (data.errors && typeof data.errors === 'object') {
              error = Object.keys(data.errors).map(function (key) {
                return Array.isArray(data.errors[key]) ? data.errors[key].join(', ') : data.errors[key];
              }).join(', ');
            }
            setError(form, error || 'Sorry, this could not be added to your cart.');
            if (cartDrawer && typeof cartDrawer.refresh === 'function') cartDrawer.refresh(true);
            return;
          }

          return loadSections(data).then(function (dataWithSections) {
            if (afterAtcMode() === 'page') {
              setTimeout(function () {
                window.location.href = cartUrl();
              }, 300);
              return;
            }

            // Update cart icon count.
            var cartIcon = document.getElementById('cart-icon-bubble');
            if (cartIcon && dataWithSections.sections && dataWithSections.sections['cart-icon-bubble']) {
              cartIcon.innerHTML = dataWithSections.sections['cart-icon-bubble'];
            }

            // Update / open the cart drawer.
            if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
              cartDrawer.renderContents(dataWithSections, afterAtcMode() === 'drawer');
            } else if (window.location.pathname === cartUrl()) {
              window.location.reload();
            }

            // Added to cart — clear the committed snapshots + summary.
            addedItems = [];
            renderSummary();

            // Let other components (free-shipping bars etc.) react.
            if (btn) {
              btn.dispatchEvent(new CustomEvent('on:cart:add', {
                bubbles: true,
                detail: { variantId: parent.id }
              }));
            }
          });
        })
        .catch(function (error) {
          console.log(error); // eslint-disable-line
          setError(form, 'Sorry, something went wrong. Please try again.');
          if (cartDrawer && typeof cartDrawer.refresh === 'function') cartDrawer.refresh(true);
        })
        .finally(function () {
          if (btn) {
            btn.classList.add('is-success');
            btn.removeAttribute('aria-disabled');
            setTimeout(function () {
              btn.classList.remove('is-loading');
              btn.classList.remove('is-success');
            }, 1400);
          }
        });
      }
    }

    // Initial label reflects the base price.
    api.instances[formId] = {
      submit: bundledSubmit
    };
    updateLabel();
    wireAtcClickBridge();
  }

  function boot() {
    document.querySelectorAll('.ttt-add-a-pet').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
